import React, { useState, useRef, useCallback } from 'react';

// Force refresh component
const TurquoiseEditor = ({ value, onChange, placeholder }) => {
  const editorRef = useRef(null);
  const [content, setContent] = useState(value || '');
  const [hoveredButton, setHoveredButton] = useState(null);
  const [activeButton, setActiveButton] = useState(null);

  const handleContentChange = useCallback((event) => {
    const newContent = event.currentTarget.innerHTML;
    setContent(newContent);
    if (onChange) {
      onChange(newContent);
    }
  }, [onChange]);

  const handleKeyUp = useCallback((event) => {
    const newContent = event.currentTarget.innerHTML;
    setContent(newContent);
    if (onChange) {
      onChange(newContent);
    }
  }, [onChange]);

  // Handle input events for nested contenteditable elements (like code blocks)
  const handleInput = useCallback((event) => {
    const newContent = editorRef.current.innerHTML;
    setContent(newContent);
    if (onChange) {
      onChange(newContent);
    }
  }, [onChange]);

  React.useEffect(() => {
    if (editorRef.current && value !== content) {
      editorRef.current.innerHTML = value || '';
      setContent(value || '');
    }
  }, [value, content]);

  // Set up event delegation for nested contenteditable elements (like code blocks)
  React.useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    const handleNestedInput = (event) => {
      // If the event came from a nested contenteditable element
      if (event.target !== editor && event.target.contentEditable === 'true') {
        const newContent = editor.innerHTML;
        setContent(newContent);
        if (onChange) {
          onChange(newContent);
        }
      }
    };

    // Add event listener for input events that bubble up from nested elements
    editor.addEventListener('input', handleNestedInput, true);
    
    return () => {
      editor.removeEventListener('input', handleNestedInput, true);
    };
  }, [onChange]);

  const toolbarStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 16px',
    background: 'rgba(75, 196, 185, 0.1)',
    border: '2px solid #4BC4B9',
    borderRadius: '20px', // Fully rounded toolbar as separate component
    flexWrap: 'wrap',
    margin: '0 0 16px 0' // Add gap between toolbar and editor
  };

  const getButtonStyle = (buttonId, isHovered, isActive) => ({
    background: isActive 
      ? '#4BC4B9' 
      : isHovered 
        ? 'rgba(75, 196, 185, 0.2)' 
        : 'transparent',
    border: '1px solid #4BC4B9',
    borderRadius: '8px',
    color: isActive ? '#1C2025' : '#4BC4B9',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: '600',
    padding: '6px 10px',
    transition: 'all 0.2s ease',
    minWidth: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
    position: 'relative',
    userSelect: 'none',
    outline: 'none',
    transform: isActive ? 'translateY(1px)' : isHovered ? 'translateY(-1px)' : 'translateY(0)',
    boxShadow: isHovered && !isActive ? '0 2px 8px rgba(75, 196, 185, 0.3)' : 'none'
  });

  const handleButtonClick = useCallback((formatCommand, formatValue = null) => {
    if (formatCommand === 'insertCodeBlock') {
      // Special handling for code block insertion
      const selection = window.getSelection();
      let selectedText = selection.toString();
      
      // Create the code block HTML with better turquoise theme integration
      const codeBlockHTML = `
        <div style="
          background: linear-gradient(145deg, #1e2832 0%, #2a3441 100%); 
          border: 2px solid #4BC4B9; 
          border-radius: 12px; 
          padding: 18px; 
          margin: 16px 0; 
          font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', 'Consolas', monospace; 
          font-size: 14px; 
          line-height: 1.5;
          color: #e8f4f8; 
          white-space: pre-wrap; 
          overflow-x: auto;
          box-shadow: 0 6px 20px rgba(75, 196, 185, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.05);
          position: relative;
          transition: all 0.3s ease;
        " onmouseover="this.style.boxShadow='0 8px 25px rgba(75, 196, 185, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.05)'" onmouseout="this.style.boxShadow='0 6px 20px rgba(75, 196, 185, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.05)'">
          <div style="
            color: #4BC4B9; 
            font-size: 11px; 
            font-weight: 600; 
            letter-spacing: 0.5px;
            margin-bottom: 12px; 
            opacity: 0.9;
            text-transform: uppercase;
          ">Code Block</div>
          <code contenteditable="true" style="
            background: transparent; 
            border: none; 
            outline: none; 
            color: inherit; 
            font-family: inherit; 
            font-size: inherit;
            width: 100%;
            display: block;
            min-height: 20px;
          ">${selectedText || ''}</code>
        </div>
        <div><br></div>
      `;
      
      // Use document.execCommand to insert HTML
      document.execCommand('insertHTML', false, codeBlockHTML);
      
      // Focus the editor
      if (editorRef.current) {
        editorRef.current.focus();
      }
    } else {
      // Standard document.execCommand for other formats
      document.execCommand(formatCommand, false, formatValue);
      if (editorRef.current) {
        editorRef.current.focus();
      }
    }
  }, [onChange]);

  const createButtonProps = useCallback((command, value = null, buttonId) => ({
    type: 'button',
    onClick: (e) => {
      e.preventDefault();
      e.stopPropagation();
      setActiveButton(buttonId);
      setTimeout(() => setActiveButton(null), 150); // Brief active state
      handleButtonClick(command, value);
    },
    onMouseDown: (e) => {
      e.preventDefault(); // Prevent losing focus from editor
    },
    onMouseEnter: () => setHoveredButton(buttonId),
    onMouseLeave: () => setHoveredButton(null),
    style: getButtonStyle(
      buttonId, 
      hoveredButton === buttonId, 
      activeButton === buttonId
    )
  }), [handleButtonClick, hoveredButton, activeButton]);

  const editorStyle = {
    minHeight: '400px',
    padding: '20px',
    border: '2px solid #4BC4B9',
    borderRadius: '20px', // Fully rounded editor as separate component
    backgroundColor: '#1C2025',
    color: '#e1e4e8',
    fontSize: '16px',
    lineHeight: '1.6',
    fontFamily: "'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', 'Consolas', monospace",
    outline: 'none',
    whiteSpace: 'pre-wrap',
    wordWrap: 'break-word',
    resize: 'vertical',
    overflowY: 'auto',
    margin: '0'
  };

  const separatorStyle = {
    width: '1px',
    height: '24px',
    background: '#4BC4B9',
    margin: '0 8px'
  };

  return (
    <div style={{ width: '100%', margin: '0', padding: '0' }}>
      <div style={toolbarStyle}>
        <button {...createButtonProps('bold', null, 'bold')} title="Bold">
          <strong>B</strong>
        </button>
        <button {...createButtonProps('italic', null, 'italic')} title="Italic">
          <em>I</em>
        </button>
        <button {...createButtonProps('underline', null, 'underline')} title="Underline">
          <u>U</u>
        </button>
        <span style={separatorStyle}>|</span>
        <button {...createButtonProps('formatBlock', 'h1', 'h1')} title="Heading 1">
          H1
        </button>
        <button {...createButtonProps('formatBlock', 'h2', 'h2')} title="Heading 2">
          H2
        </button>
        <button {...createButtonProps('formatBlock', 'h3', 'h3')} title="Heading 3">
          H3
        </button>
        <span style={separatorStyle}>|</span>
        <button {...createButtonProps('insertCodeBlock', null, 'code')} title="Insert Code Block">
          &lt;/&gt;
        </button>
        <span style={separatorStyle}>|</span>
        <button {...createButtonProps('insertUnorderedList', null, 'ul')} title="Bullet List">
          • List
        </button>
        <button {...createButtonProps('insertOrderedList', null, 'ol')} title="Numbered List">
          1. List
        </button>
      </div>
      <div
        ref={editorRef}
        contentEditable={true}
        suppressContentEditableWarning={true}
        onInput={handleInput}
        onChange={handleContentChange}
        onKeyUp={handleKeyUp}
        onBlur={handleContentChange}
        style={editorStyle}
        placeholder={placeholder || "Start typing your note..."}
      />
    </div>
  );
};

export default TurquoiseEditor;
